/**
 * Top menu model built from the ObjectTab rows (state.tabs in the Store).
 *
 * A tab is identified by `key` (rows created before keys existed fall back to
 * their `schema`). Optional fields, all opt-in through the business preset or
 * Configuración › Pestañas:
 *   group  — menu group key; with SHOW_NAV_GROUPS the menu renders one
 *            dropdown per group, labelled 'nav.group.<group>'
 *   filter — fixed filter the target list applies; the link carries
 *            ?view=<key> and the list sends the filter to its search endpoint
 *   path   — explicit route for a tab that is not a schema list
 * Without groups (or with the flag off) the model is exactly the historical
 * flat list of active tabs sorted by order.
 */

export const tabKey = (tab) => (tab && (tab.key || tab.schema)) || '';

export const hasTabFilter = (tab) =>
	Boolean(tab && tab.filter && typeof tab.filter === 'object' && Object.keys(tab.filter).length > 0);

/** Route of a tab: its path, else its schema list (+ ?view=<key> when filtered). */
export const tabLink = (tab) => {
	if (tab.path) return tab.path;
	const base = `/AdminScreen/${tab.schema}`;
	return hasTabFilter(tab) ? `${base}?view=${encodeURIComponent(tabKey(tab))}` : base;
};

/** Tab filter → search query params ({ concepts: ['a', 'b'] } → { concepts: 'a,b' }). */
export const tabFilterParams = (filter) => {
	if (!filter || typeof filter !== 'object') return {};
	const params = {};
	Object.entries(filter).forEach(([name, value]) => {
		if (value === undefined || value === null || value === '') return;
		params[name] = Array.isArray(value) ? value.join(',') : String(value);
	});
	return params;
};

export const tabFilterQuery = (filter) => new URLSearchParams(tabFilterParams(filter)).toString();

export const findTabByKey = (tabs, key) =>
	(key && Array.isArray(tabs) && tabs.find((tab) => tabKey(tab) === key)) || null;

/**
 * Key of the tab the current location belongs to, or null. Explicit paths win
 * (longest prefix); otherwise the /AdminScreen/<section> segment picks the tabs
 * of that schema, ?view=<key> picks among them and, without a view, the
 * unfiltered one is the active tab (historical behavior).
 */
export const activeTabKey = (tabs, { pathname = '', search = '' } = {}) => {
	const list = Array.isArray(tabs) ? tabs : [];
	const path = pathname.toLowerCase();
	const byPath = list
		.filter((tab) => {
			if (!tab.path) return false;
			const tabPath = tab.path.toLowerCase();
			return path === tabPath || path.startsWith(`${tabPath}/`);
		})
		.sort((a, b) => b.path.length - a.path.length)[0];
	if (byPath) return tabKey(byPath);

	const parts = path.split('/');
	const index = parts.indexOf('adminscreen');
	const section = index >= 0 ? parts[index + 1] : undefined;
	if (!section) return null;
	const candidates = list.filter((tab) => !tab.path && tab.schema && tab.schema.toLowerCase() === section);
	const view = new URLSearchParams(search).get('view');
	const byView = view && candidates.find((tab) => tabKey(tab) === view);
	if (byView) return tabKey(byView);
	const plain = candidates.find((tab) => !hasTabFilter(tab));
	return plain ? tabKey(plain) : null;
};

// Known ERP groups keep this order whatever the order of the tabs inside them
// (presets reuse older tabs whose order was set for the flat menu).
export const NAV_GROUP_ORDER = ['ventas', 'compras', 'personal', 'finanzas', 'catalogo'];
const groupRank = (key) => {
	const i = NAV_GROUP_ORDER.indexOf(key);
	return i === -1 ? NAV_GROUP_ORDER.length : i;
};

/**
 * Menu items: { type: 'link', key, tab, to } or { type: 'group', key, items }.
 * Ungrouped tabs come first, as plain links in tab order; then the groups —
 * known ERP groups in NAV_GROUP_ORDER, any other group after them where its
 * lowest-order tab would be.
 */
export const buildNavModel = (tabs, { groupsEnabled = false } = {}) => {
	const active = (Array.isArray(tabs) ? tabs : [])
		.filter((tab) => tab.active)
		.sort((a, b) => a.order - b.order);
	const toLink = (tab) => ({ type: 'link', key: tabKey(tab), tab, to: tabLink(tab) });

	if (!groupsEnabled || !active.some((tab) => tab.group)) {
		return active.map(toLink);
	}

	const items = [];
	const groups = new Map();
	active.forEach((tab) => {
		if (!tab.group) {
			items.push(toLink(tab));
			return;
		}
		let group = groups.get(tab.group);
		if (!group) {
			group = { type: 'group', key: tab.group, items: [] };
			groups.set(tab.group, group);
			items.push(group);
		}
		group.items.push(toLink(tab));
	});
	const links = items.filter((item) => item.type === 'link');
	const grouped = items.filter((item) => item.type === 'group');
	const position = new Map(grouped.map((group, i) => [group.key, i]));
	grouped.sort((a, b) => groupRank(a.key) - groupRank(b.key) || position.get(a.key) - position.get(b.key));
	return [...links, ...grouped];
};
